import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Loader2, Pencil, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ShippingCity {
  id: string;
  name: string;
  name_arabic: string | null;
  shipping_fee: number;
  is_active: boolean;
  created_at: string;
}

const AdminShipping = () => {
  const [cities, setCities] = useState<ShippingCity[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState({ name: "", name_arabic: "", shipping_fee: 0 });
  const [newCity, setNewCity] = useState({ name: "", name_arabic: "", shipping_fee: 0 });
  const [showAdd, setShowAdd] = useState(false);
  const { toast } = useToast();

  useEffect(() => { fetchCities(); }, []);

  const fetchCities = async () => {
    try {
      const { data, error } = await supabase
        .from("shipping_cities")
        .select("*")
        .order("name");
      if (error) throw error;
      setCities(data || []);
    } catch (error) {
      console.error("Error fetching cities:", error);
      toast({ title: "Error", description: "Failed to load shipping cities", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const addCity = async () => {
    if (!newCity.name) return;
    try {
      const { error } = await supabase.from("shipping_cities").insert({
        name: newCity.name,
        name_arabic: newCity.name_arabic || null,
        shipping_fee: newCity.shipping_fee,
      });
      if (error) throw error;
      toast({ title: "Success", description: "City added" });
      setNewCity({ name: "", name_arabic: "", shipping_fee: 0 });
      setShowAdd(false);
      fetchCities();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const startEdit = (city: ShippingCity) => {
    setEditingId(city.id);
    setEditData({ name: city.name, name_arabic: city.name_arabic || "", shipping_fee: city.shipping_fee });
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      const { error } = await supabase.from("shipping_cities").update({
        name: editData.name,
        name_arabic: editData.name_arabic || null,
        shipping_fee: editData.shipping_fee,
      }).eq("id", editingId);
      if (error) throw error;
      toast({ title: "Success", description: "City updated" });
      setEditingId(null);
      fetchCities();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase.from("shipping_cities").update({ is_active: !isActive }).eq("id", id);
      if (error) throw error;
      setCities(cities.map(c => c.id === id ? { ...c, is_active: !isActive } : c));
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  const deleteCity = async (id: string) => {
    if (!confirm("Delete this city?")) return;
    try {
      const { error } = await supabase.from("shipping_cities").delete().eq("id", id);
      if (error) throw error;
      toast({ title: "Success", description: "City deleted" });
      fetchCities();
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-serif">Shipping Cities</h1>
        <Button variant="bronze" onClick={() => setShowAdd(!showAdd)}>
          <Plus className="w-4 h-4 mr-2" />Add City
        </Button>
      </div>

      <AnimatePresence>
        {showAdd && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-card border border-border rounded-sm p-4 mb-6 space-y-4"
          >
            <h3 className="font-serif text-lg">Add New City</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input placeholder="City name (English)" value={newCity.name} onChange={(e) => setNewCity({ ...newCity, name: e.target.value })} />
              <Input placeholder="City name (Arabic)" value={newCity.name_arabic} onChange={(e) => setNewCity({ ...newCity, name_arabic: e.target.value })} />
              <Input type="number" placeholder="Shipping fee (EGP)" value={newCity.shipping_fee || ""} onChange={(e) => setNewCity({ ...newCity, shipping_fee: parseFloat(e.target.value) || 0 })} />
            </div>
            <div className="flex gap-2">
              <Button variant="bronze" onClick={addCity}>Add</Button>
              <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">City</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Arabic</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Fee (EGP)</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Active</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {cities.map((city) => (
                  <motion.tr
                    key={city.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="border-b border-border/50 hover:bg-muted/20 transition-colors"
                  >
                    {editingId === city.id ? (
                      <>
                        <td className="px-4 py-3"><Input value={editData.name} onChange={(e) => setEditData({ ...editData, name: e.target.value })} /></td>
                        <td className="px-4 py-3"><Input value={editData.name_arabic} onChange={(e) => setEditData({ ...editData, name_arabic: e.target.value })} /></td>
                        <td className="px-4 py-3"><Input type="number" value={editData.shipping_fee || ""} onChange={(e) => setEditData({ ...editData, shipping_fee: parseFloat(e.target.value) || 0 })} /></td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={saveEdit}><Check className="w-4 h-4 text-green-500" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => setEditingId(null)}><X className="w-4 h-4" /></Button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 font-medium">{city.name}</td>
                        <td className="px-4 py-3 text-muted-foreground">{city.name_arabic || "—"}</td>
                        <td className="px-4 py-3">{city.shipping_fee} EGP</td>
                        <td className="px-4 py-3">
                          <Switch checked={city.is_active} onCheckedChange={() => toggleActive(city.id, city.is_active)} />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <Button variant="ghost" size="icon" onClick={() => startEdit(city)}><Pencil className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="icon" onClick={() => deleteCity(city.id)} className="text-destructive hover:text-destructive/80"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </td>
                      </>
                    )}
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
          {cities.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              No shipping cities configured yet. Add cities to enable shipping fee calculation.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminShipping;
